// Bean Card (§7.4, §14.2): the profile screen. Your rendered bean, name and Bean Level with the XP
// bar, Bonk Bucks, lifetime totals (matches, wins, rounds, KOs, CLUTCH saves, longest flight,
// oldest peel) with the favorite map as the eighth tile, today's 3 challenges with progress and rewards, and your last
// Tapestry postcard. CHANGE NAME and LOCKER are one press away.
import { MapId } from '@shared/ids';
import type { LiveScreen } from '../contracts';
import type { UiDeps } from '../deps';
import { beanIcon, icon, identityHex } from '../icons';
import { mapName } from '../labels';
import { el, setStyle, setText } from '../ui';
import { countUp } from '../anim';
import { button, closeButton } from '../widgets';
import { mapThumb } from '../mapThumbs';

function stat(label: string, ic: string): { el: HTMLElement; value: HTMLElement } {
  const value = el('span', { class: 'bc-stat-v' });
  return { el: el('div', { class: 'bc-stat' }, el('span', { class: 'bc-stat-ico' }, icon(ic)), value, el('span', { class: 'bc-stat-l', text: label })), value };
}

export function create(deps: UiDeps): LiveScreen {
  const portrait = el('div', { class: 'bc-portrait' });
  const name = el('div', { class: 'bc-name' });
  const lvl = el('span', { class: 'bc-lvl-num' });
  const xpFill = el('i');
  const xpText = el('div', { class: 'bc-xp-text' });
  const bucks = el('span', { class: 'bc-bucks-num' });
  const stats = {
    matches: stat('Matches', 'flag'), wins: stat('Wins', 'trophy'), rounds: stat('Rounds', 'timer'), kos: stat('KOs', 'fist'),
    clutches: stat('CLUTCH saves', 'heart'), flight: stat('Longest flight', 'plane'), peel: stat('Oldest peel', 'peelclock'),
  };
  // The favorite map is the eighth lifetime tile: its thumbnail is the tile's icon.
  const favArt = el('span', { class: 'bc-stat-ico bc-fav-art' });
  const favName = el('span', { class: 'bc-stat-v bc-fav-name ellipsis' });
  const favSub = el('span', { class: 'bc-stat-l' });
  const fav = el('div', { class: 'bc-stat bc-fav' }, favArt, favName, favSub);
  const challenges = el('div', { class: 'bc-challenges' });
  const postcard = el('div', { class: 'bc-postcard' });

  const hero = el('div', { class: 'bc-hero' }, portrait,
    el('div', { class: 'bc-id' },
      name,
      el('div', { class: 'bc-level' }, el('span', { class: 'lvl-badge big' }, el('small', { text: 'LV' }), lvl), el('div', { class: 'bc-xp' }, el('div', { class: 'bar xp' }, xpFill), xpText)),
      el('div', { class: 'row wrap bc-chips' }, el('span', { class: 'chip yellow' }, icon('coin'), bucks, el('span', { text: 'Bucks' })),
        button({ label: 'CHANGE NAME', icon: 'profile', variant: 'cream', size: 'small', onClick: () => deps.nav.show('name', { edit: true }) }),
        button({ label: 'LOCKER', icon: 'shirt', variant: 'grape', size: 'small', onClick: () => deps.nav.show('locker') }))));

  const body = el('div', { class: 'sheet-body bc-body' },
    hero,
    el('div', { class: 'bc-grid' },
      el('div', { class: 'bc-col' }, el('div', { class: 'bc-sec-title', text: 'LIFETIME' }), el('div', { class: 'bc-stats' }, ...Object.values(stats).map((s) => s.el), fav)),
      el('div', { class: 'bc-col' }, el('div', { class: 'bc-sec-title', text: "TODAY'S CHALLENGES" }), challenges, el('div', { class: 'bc-sec-title', text: 'LAST POSTCARD' }), postcard)));

  const panel = el('div', { class: 'panel sheet with-ribbon beancard-panel springy' },
    el('div', { class: 'ribbon', text: 'BEAN CARD' }), closeButton(() => deps.nav.back()), body,
    el('div', { class: 'sheet-foot row center' }, button({ label: 'BACK', icon: 'back', variant: 'cream', autofocus: true, onClick: () => deps.nav.back() })));
  const root = el('div', { class: 'screen modal' }, panel);

  function render(animate: boolean): void {
    const p = deps.profile();
    const lv = deps.services.profiles.level();
    setText(name, p.name || 'New Bean');
    setText(lvl, String(lv.level));
    setStyle(xpFill, 'width', `${Math.round(lv.frac * 100)}%`);
    setText(xpText, lv.capped ? `MAX LEVEL • ${lv.xp} / ${lv.need} bonus XP` : `${lv.xp} / ${lv.need} XP to Level ${lv.level + 1}`);
    if (animate) countUp(bucks, 0, p.bucks, 700);
    else setText(bucks, String(p.bucks));
    const pref = p.cosmetics.identityPref < 8 ? p.cosmetics.identityPref : 0;
    portrait.replaceChildren(deps.services.portrait(p.cosmetics, pref, 256) ?? beanIcon(identityHex(pref, false), 'happy', 'ico bc-fallback'));
    const s = p.stats;
    const vals: [HTMLElement, number, (n: number) => string][] = [
      [stats.matches.value, s.matches, (n) => String(Math.round(n))], [stats.wins.value, s.wins, (n) => String(Math.round(n))],
      [stats.rounds.value, s.rounds, (n) => String(Math.round(n))], [stats.kos.value, s.kos, (n) => String(Math.round(n))],
      [stats.clutches.value, s.clutches, (n) => String(Math.round(n))], [stats.flight.value, s.longestFlight, (n) => `${n.toFixed(1)} m`],
      [stats.peel.value, s.oldestPeelTicks / 60, (n) => `${n.toFixed(1)} s`],
    ];
    for (const [node, v, fmt] of vals) {
      if (animate) countUp(node, 0, v, 650, fmt);
      else setText(node, fmt(v));
    }
    // Favorite map: most rounds played on an arena.
    let best = -1;
    let bestN = 0;
    s.mapRounds.forEach((n, id) => {
      if (id >= MapId.TEACUP && n > bestN) {
        bestN = n;
        best = id;
      }
    });
    favArt.replaceChildren(best >= 0 ? mapThumb(best as MapId) : icon('question'));
    setText(favName, best >= 0 ? mapName(best as MapId) : '???');
    setText(favSub, best >= 0 ? `Favorite map • ${bestN} round${bestN === 1 ? '' : 's'}` : 'Favorite map: play to find out!');
    challenges.replaceChildren(...deps.services.profiles.challenges().map((c) => el('div', { class: `bc-ch${c.done ? ' done' : ''}` },
      el('span', { class: 'bc-ch-box' }, c.done ? icon('checkBadge') : icon('flag')),
      el('div', { class: 'bc-ch-main' },
        el('div', { class: 'bc-ch-text', text: c.text }),
        el('div', { class: 'bar gold bc-ch-bar' }, el('i', { style: { width: `${Math.round((100 * c.progress) / Math.max(1, c.goal))}%` } })),
        el('div', { class: 'bc-ch-meta' }, el('span', { text: c.done ? 'COMPLETE!' : c.goal === 1 ? 'Not yet — go get it!' : `${c.progress} / ${c.goal} ${c.unit}` }), el('span', { class: 'bc-ch-reward', text: `+${c.xp} XP • +${c.bucks}` }, icon('coin')))))));
    const url = p.lastPostcard;
    postcard.replaceChildren(url ? el('img', { class: 'bc-postcard-img', attrs: { src: url, alt: 'Your last Tapestry postcard' } })
      : el('div', { class: 'bc-empty-card' }, icon('postcard'), el('span', { text: 'Finish a match to get your first postcard' })));
  }

  deps.bus.on('profile', () => {
    if (!root.hidden) render(false);
  });

  return {
    el: root,
    onShow() {
      render(true);
    },
  };
}
